/**
 * The bracket as a director reads and configures it: what each round is
 * called, how a bracket's games group into rounds, and the choices behind
 * "how many teams advance" (spec §7.10, §10).
 *
 * Pure and DOM-free, so both the admin console and the public bracket page can
 * share one answer. The arithmetic itself belongs to `@alpha/engine`
 * (`bracketShape`); nothing here re-derives it.
 */
import type { BracketShape, ResolvedBracketGame } from "@alpha/engine";

/**
 * What a round is called, named from the END of the bracket rather than the
 * start: the last round is the Final whether the bracket has two teams or
 * sixteen, and "Round 2" means nothing to someone looking for the semis.
 * Anything earlier than the quarterfinals keeps its number, because "Round of
 * 32" starts guessing at a size the caller has not been told.
 */
export function roundName(round: number, maxRound: number): string {
  const fromEnd = maxRound - round;
  if (fromEnd === 0) return "Final";
  if (fromEnd === 1) return "Semifinals";
  if (fromEnd === 2) return "Quarterfinals";
  return `Round ${round}`;
}

export interface BracketRound {
  round: number;
  /** `roundName` for this round, given the bracket's last round. */
  name: string;
  /** The round's games, in bracket position order. */
  games: ResolvedBracketGame[];
}

/**
 * A resolved bracket's games grouped into rounds, earliest first.
 *
 * The admin list is the reason this exists: fifteen bracket games in one flat
 * column are fifteen rows a director has to count through to find the one they
 * meant to give a time to. Grouped, "the second semifinal" is a place on the
 * page.
 *
 * Rounds come from the games themselves rather than from a bracket size, so a
 * bracket whose first round was pruned to byes names its rounds off the rounds
 * that exist.
 */
export function bracketRounds(games: ResolvedBracketGame[]): BracketRound[] {
  const byRound = new Map<number, ResolvedBracketGame[]>();
  for (const g of games) {
    const list = byRound.get(g.round);
    if (list) list.push(g);
    else byRound.set(g.round, [g]);
  }

  const rounds = [...byRound.keys()].sort((a, b) => a - b);
  const maxRound = rounds[rounds.length - 1] ?? 1;
  return rounds.map((round) => ({
    round,
    name: roundName(round, maxRound),
    games: [...byRound.get(round)!].sort((a, b) => a.position - b.position),
  }));
}

/** One choice in the "teams advancing" picker. `value` is the `<option value>`. */
export interface AdvanceOption {
  value: string;
  label: string;
}

/** The `<option value>` for "every team in the division". */
export const ADVANCE_ALL = "all";

/**
 * The cuts a director may pick for a division, bounded by the teams it has.
 *
 * Two goes first among the numbers because a bracket needs somebody to
 * eliminate — the API refuses `1` — and the list stops one short of the team
 * count, since "top N of N" is what "All teams" already says.
 *
 * `current` is included even when it falls outside that range, which is the
 * case that bites: a director sets a top-8 cut and later removes entries, and
 * a `<select>` whose value matches no option renders as its first one. That
 * would show "All teams" over a stored cut of 8 and write it on the next
 * change the director made to something else entirely.
 */
export function advanceOptions(teamCount: number, current: "all" | number): AdvanceOption[] {
  const cuts = new Set<number>();
  for (let n = 2; n < teamCount; n++) cuts.add(n);
  if (typeof current === "number" && current >= 2) cuts.add(current);

  return [
    { value: ADVANCE_ALL, label: teamCount > 0 ? `All teams (${teamCount})` : "All teams" },
    ...[...cuts].sort((a, b) => a - b).map((n) => ({ value: String(n), label: `Top ${n}` })),
  ];
}

/** The picker's value as the API takes it. Anything unparseable means "all". */
export function parseAdvance(value: string): "all" | number {
  if (value === ADVANCE_ALL) return "all";
  const n = Number(value);
  return Number.isInteger(n) && n >= 2 ? n : "all";
}

/** `n thing` / `n things`, for the summary below. */
const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/**
 * What the current setup produces, in one line: "8-team bracket · 2 byes · 3
 * rounds · 7 games". The point is that a cut is a decision with consequences a
 * director should not have to work out — dropping from 8 to 6 is two byes and
 * two fewer games to find slots for.
 *
 * Byes appear only when there are some, because "0 byes" is noise on the
 * clean power-of-two field that is the common case.
 */
export function bracketShapeSummary(shape: BracketShape): string {
  if (shape.games === 0) return "Not enough teams for a bracket yet";
  return [
    `${shape.advancing}-team bracket`,
    ...(shape.byes > 0 ? [count(shape.byes, "bye")] : []),
    count(shape.rounds, "round"),
    count(shape.games, "game"),
  ].join(" · ");
}
